import * as THREE from "three";
import { SVGLoader } from "three/examples/jsm/loaders/SVGLoader";
import goldPrizeCalculator from "../goldPlateUtils";
import { BufferGeoUtils } from "utils-three-js";
import { scaleSVGPath } from "./makerJsFunc";
import makerjs from "makerjs";
import { removeObjWithChildren } from "../commonUtils.js/commonFunc";

export function getPointsOfPaths(inPaths) {
    let points = [];

    inPaths.forEach((path) => {
        let subPaths = path.subPaths;
        subPaths.forEach((subPath) => {
            let curves = subPath.curves;
            let subPathPoints = [];
            curves.forEach((curve) => {
                let points = curve.getPoints(100);
                let pointsArr = points.map((point) => [point.x, point.y]);
                subPathPoints.push(...pointsArr);
            });

            points.push(subPathPoints);
        });
    });
    return points;
}
export function getPointsForThreejsPaths(inPaths) {
    // THREE js path
    let points = [];

    inPaths.forEach((path) => {
        let subPaths = path.subPaths;
        subPaths.forEach((subPath) => {
            let subPathPoints = subPath.getPoints();
            points.push(...subPathPoints.map((point) => [point.x, point.y]));
        });
    });
    return points;
}

export function removeIndividualChars(inScene) {
    while (inScene.getObjectByName("individualMesh")) {
        removeObjWithChildren(inScene.getObjectByName("individualMesh"));
    }
}
export function loadSeparateChar(
    inSVG,
    inScene,
    inMat,
    inExtrudeSettings,
    language,
    textGlyphs
) {
    let loader2 = new SVGLoader();

    let paths2 = loader2.parse(inSVG).paths;
    let shapes2 = paths2[0].toShapes(true);
    let individualMeshes = [];
    shapes2.forEach((shape) => {
        let geometry = new THREE.ExtrudeGeometry(shape, inExtrudeSettings);
        const mesh = new THREE.Mesh(geometry, inMat);
        mesh.rotateX(Math.PI);
        mesh.name = "individualMesh";
        individualMeshes.push(mesh);
    });
    const remainingMeshes = removeTriangularMesh(individualMeshes);

    // Sort meshes based on the position of their center
    remainingMeshes.sort((a, b) => {
        const aCenter = new THREE.Vector3();
        const bCenter = new THREE.Vector3();
        a.geometry.boundingBox.getCenter(aCenter);
        b.geometry.boundingBox.getCenter(bCenter);
        return aCenter.x - bCenter.x;
    });

    for (let i = 0; i < remainingMeshes.length; i++) {
        if (language === "Arabic") {
            remainingMeshes[i].visible = false;
        }
        // remainingMeshes[i].name =``
        inScene.add(remainingMeshes[i]);
    }

    return remainingMeshes;
}

export function fitToPlateSize(
    mesh,
    individualMeshes,
    shapes,
    svgPathData,
    fontThickness,
    plateSize,
    deviationData = null
) {
    let shapeGeo = new THREE.ShapeGeometry(shapes);
    let mat = new THREE.MeshBasicMaterial({
        color: 0x000000,
        side: THREE.DoubleSide,
    });
    let shapeMesh = new THREE.Mesh(shapeGeo, mat);
    let bBox = new THREE.Box3().setFromObject(shapeMesh);
    let size = bBox.getSize(new THREE.Vector3());
    let pWidth = goldPrizeCalculator.getPWidth();
    let pHeight = goldPrizeCalculator.getPHeight();
    let yRatio = pHeight / size.y;

    mesh.scale.set(yRatio, yRatio, 1);
    shapeMesh.scale.set(yRatio, yRatio, 1);

    individualMeshes?.forEach((mesh) => {
        mesh.scale.set(yRatio, yRatio, 1);
    });

    let newBBox = new THREE.Box3().setFromObject(shapeMesh);
    let newSize = newBBox.getSize(new THREE.Vector3());
    let combinedRatio = yRatio;
    if (newSize.x > pWidth) {
        let xRatio = pWidth / newSize.x;
        combinedRatio = xRatio * yRatio;
        mesh.scale.set(combinedRatio, combinedRatio, 1);
        shapeMesh.scale.set(combinedRatio, combinedRatio, 1);
        individualMeshes?.forEach((mesh) => {
            mesh.scale.set(combinedRatio, combinedRatio, 1);
        });
    }
    shapeMesh.scale.set(1, 1, 1);
    shapeGeo.applyMatrix4(
        new THREE.Matrix4().makeScale(combinedRatio, combinedRatio, 1)
    );
    shapeMesh.updateMatrix();
    shapeMesh.updateMatrixWorld();

    let area = BufferGeoUtils.getSurfaceArea(shapeGeo);

    goldPrizeCalculator.setPThickness(fontThickness);
    let weight = goldPrizeCalculator.getWeightFromSurfaceArea(area);

    if (deviationData) {
        // weight += deviationData.weight;
        goldPrizeCalculator.addExtraWeight(weight, plateSize, deviationData);
    }

    let scaledPath = scaleSVGPath(svgPathData, combinedRatio);

    let bbox = makerjs.measure.modelExtents(
        makerjs.importer.fromSVGPathData(scaledPath)
    );
    let width = bbox.width.toFixed(2);
    let height = bbox.height.toFixed(2);
    let viewBox = `0 0 ${width} ${height}`;
    let svgString = `<svg  fill-rule="evenodd" xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="${viewBox}">
        <path d="${scaledPath}" fill="black" />
    </svg>`;

    return {
        combinedRatio,
        weight,
        svgString,
        width,
        height,
        area,
    };
}

export function removeTriangularMesh(meshes) {
    const remainingMeshes = [];

    for (let i = 0; i < meshes.length; i++) {
        const boundingBox1 = new THREE.Box3().setFromObject(meshes[i]);
        let isContained = false;

        for (let j = 0; j < meshes.length; j++) {
            if (i !== j) {
                const boundingBox2 = new THREE.Box3().setFromObject(meshes[j]);
                if (
                    boundingBox1.max.x < boundingBox2.max.x &&
                    boundingBox1.max.y < boundingBox2.max.y &&
                    boundingBox1.min.x > boundingBox2.min.x &&
                    boundingBox1.min.y > boundingBox2.min.y
                ) {
                    isContained = true;
                    break;
                }
            }
        }

        if (!isContained) {
            remainingMeshes.push(meshes[i]);
        } else {
            meshes[i].geometry.dispose();
            meshes[i].material.dispose();
            meshes[i].removeFromParent();
        }
    }

    return remainingMeshes;
}
